/**
 * The pure edit catalog, bound to the editor state's mutate: a catalog
 * function `(room, ...args) => room` becomes a stateless mutator
 * `(...args) => void` — the current plan is read from the state and the
 * result committed. Derived MECHANICALLY so the two surfaces cannot drift.
 */
import {
  addFreeElement,
  addSegment,
  FloorPlanRoom,
  removeFreeElement,
  removeSegment,
  replaceFreeElement,
  setAlcoveDepth,
  setAlcoveEndGap,
  setAlcoveLeftWall,
  setAlcoveOuterChamfer,
  setAlcovePosition,
  setAlcoveRightWall,
  setAlcovesMaxDepth,
  setAlcoveStartGap,
  setAlcoveWidth,
  setCeilingHeight,
  setFreeElementBase,
  setFreeElementCornerChamfer,
  setFreeElementFloorType,
  setFreeElementFrame,
  setFreeElementGap,
  setFreeElementHeight,
  setFreeElementObjectProps,
  setFreeElementPosition,
  setFreeElementRotation,
  setFreeElementSize,
  setRoomCornerChamfer,
  setRoomFloorType,
  setRoomLength,
  setRoomWidth,
  setSegmentEndGap,
  setSegmentOpeningBase,
  setSegmentOpeningHeight,
  setSegmentPosition,
  setSegmentStart,
  setSegmentStartGap,
  setSegmentSwing,
  setSegmentType,
  setSegmentWidth,
} from '@kaiizen/kai-floor-plan';
import type { EditorMutableState } from '../editor-model';

/** The pure edit catalog, as one object — the source the bound edits mirror. */
const CATALOG = {
  addFreeElement,
  addSegment,
  removeFreeElement,
  removeSegment,
  replaceFreeElement,
  setAlcoveDepth,
  setAlcoveEndGap,
  setAlcoveLeftWall,
  setAlcoveOuterChamfer,
  setAlcovePosition,
  setAlcoveRightWall,
  setAlcoveStartGap,
  setAlcoveWidth,
  setAlcovesMaxDepth,
  setCeilingHeight,
  setFreeElementBase,
  setFreeElementCornerChamfer,
  setFreeElementFloorType,
  setFreeElementFrame,
  setFreeElementGap,
  setFreeElementHeight,
  setFreeElementObjectProps,
  setFreeElementPosition,
  setFreeElementRotation,
  setFreeElementSize,
  setRoomCornerChamfer,
  setRoomFloorType,
  setRoomLength,
  setRoomWidth,
  setSegmentEndGap,
  setSegmentOpeningBase,
  setSegmentOpeningHeight,
  setSegmentPosition,
  setSegmentStart,
  setSegmentStartGap,
  setSegmentSwing,
  setSegmentType,
  setSegmentWidth,
} as const;

type Bound<F> = F extends (room: FloorPlanRoom, ...args: infer A) => FloorPlanRoom ? (...args: A) => void
  : never;
export type FloorPlanEdits = { [K in keyof typeof CATALOG]: Bound<typeof CATALOG[K]> };

/** Bind the whole catalog over a mutate function (one place, built once). */
export function makeFloorPlanEdits(
  mutate: (mutateEditorState: (editorState: EditorMutableState) => void) => void,
): FloorPlanEdits {
  return Object.fromEntries(
    Object.entries(CATALOG).map(([editName, applyEdit]) => [
      editName,
      (...editArguments: unknown[]) =>
        mutate(editorState => {
          editorState.shape = (applyEdit as (room: FloorPlanRoom, ...remainingArguments: unknown[]) => FloorPlanRoom)(editorState.shape, ...editArguments);
        }),
    ]),
  ) as FloorPlanEdits;
}
